// Admin console: every burn across all tenants.
//
// Answers the question an operator could not answer before — "did anything
// actually get burned?". A burn was previously visible only to the customer
// who owned the bucket, so confirming one meant impersonating them.
//
// Each row carries the DMD digest and size: that pair is the evidence that a
// specific sealed image exists, as opposed to a commit merely having been
// requested. Glass is write-once, so this list is append-only in practice.
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { adminApi } from '../api/adminClient';
import type { AdminSnapshot } from '../api/types';
import { usePaginated } from '../lib/hooks';
import { useLang } from '../lib/useLang';
import { formatBytes, formatDateTime, formatNumber, shortHash } from '../lib/format';
import { PageHeader } from '../components/PageHeader';
import { ApiState, EmptyState } from '../components/States';
import { LoadMore } from '../components/Pagination';
import { Badge, Card } from '../components/ui';
import { IconArchive, IconSearch } from '../components/Icons';

const PAGE = 30;

export function AdminBurns() {
  const { t } = useTranslation();
  const lang = useLang();
  const [q, setQ] = useState('');
  const list = usePaginated((cursor) => adminApi.adminSnapshots(PAGE, cursor, q || undefined), [q]);

  return (
    <>
      <PageHeader title={t('admin.burns.title')} subtitle={t('admin.burns.subtitle')} />

      <Card>
        <div className="card__body">
          <label className="row" style={{ gap: 8, alignItems: 'center' }}>
            <IconSearch width={16} height={16} />
            <input
              className="input"
              value={q}
              placeholder={t('admin.burns.searchPlaceholder')}
              onChange={(e) => setQ(e.target.value)}
            />
          </label>
        </div>

        <ApiState
          status={list.status}
          error={list.error}
          data={list.status === 'error' ? null : list.items}
          onRetry={list.reload}
        >
          {(items: AdminSnapshot[]) =>
            items.length === 0 ? (
              <EmptyState title={t('admin.burns.empty')} />
            ) : (
              <>
                <div className="table-wrap">
                  <table className="data">
                    <thead>
                      <tr>
                        <th>{t('admin.burns.when')}</th>
                        <th>{t('admin.burns.customer')}</th>
                        <th>{t('admin.burns.bucket')}</th>
                        <th className="num">{t('admin.burns.seq')}</th>
                        <th className="num">{t('admin.burns.objects')}</th>
                        <th className="num">{t('admin.burns.dmdSize')}</th>
                        <th>{t('admin.burns.digest')}</th>
                        <th>{t('admin.burns.trigger')}</th>
                        <th>{t('admin.burns.by')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((s) => (
                        <tr key={s.snapshotId}>
                          <td>{formatDateTime(s.createdAt, lang)}</td>
                          <td className="cell-strong">
                            <Link to={`/customers/${encodeURIComponent(s.tenantId)}`}>
                              {s.tenantName || s.tenantId}
                            </Link>
                          </td>
                          <td>
                            <span className="row" style={{ gap: 6, alignItems: 'center' }}>
                              <IconArchive width={14} height={14} />
                              {s.archiveName}
                            </span>
                          </td>
                          <td className="num">#{s.seq}</td>
                          <td className="num">{formatNumber(s.objects, lang)}</td>
                          <td className="num">{formatBytes(s.dmdSize, lang)}</td>
                          {/* The digest is what makes this a confirmation
                              rather than a claim, so it is shown, not hidden
                              behind a detail view. */}
                          <td>
                            <code title={s.dmdSha256} style={{ fontSize: 12 }}>
                              {shortHash(s.dmdSha256)}
                            </code>
                          </td>
                          <td>
                            <Badge tone={s.trigger === 'scheduled' ? 'info' : 'accent'}>
                              {t(`admin.burns.triggerKind.${s.trigger}`, s.trigger)}
                            </Badge>
                          </td>
                          <td className="muted">{s.createdBy}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <LoadMore hasMore={list.hasMore} loading={list.loadingMore} onLoadMore={list.loadMore} />
              </>
            )
          }
        </ApiState>
      </Card>
    </>
  );
}
